// Streaming BW64 / RF64 writer.
//
// A recording of 6-8 h at 48 kHz easily exceeds 4 GB, which classic WAV cannot
// express (its RIFF chunk sizes are 32-bit). BW64 / RF64 (ITU-R BS.2088) is the
// 64-bit successor and *is* Broadcast Wave, so it still carries a `bext` chunk
// with the recording's absolute start time. Every serious DAW (Reaper, Nuendo,
// Pro Tools, ...) reads it and spots the clip on the timeline automatically.
//
// This writer streams straight to a File System Access `FileSystemWritableFile
// Stream`, so memory stays flat regardless of file length. The exact 64-bit
// sizes are not known up front (the recorder streams FLAC, so total_samples may
// be 0 in STREAMINFO), so the `ds64` size fields are written as placeholders and
// patched in `finalize()` by seeking back to their fixed offsets.

const RF64_SENTINEL = 0xffffffff; // "size lives in ds64" marker

// Writes an ASCII string into a fixed-length, NUL-padded field of `view`.
const writeFixedAscii = (view, offset, str, length) => {
    const s = (str || '').slice(0, length);
    for (let i = 0; i < length; i++) {
        view.setUint8(offset + i, i < s.length ? (s.charCodeAt(i) & 0x7f) : 0);
    }
};

// Writes a 64-bit little-endian unsigned integer (JS-number range is plenty for
// file sizes well beyond 4 GB, up to 2^53).
const writeUint64LE = (view, offset, value) => {
    const low = value >>> 0;
    const high = Math.floor(value / 0x100000000) >>> 0;
    view.setUint32(offset, low, true);
    view.setUint32(offset + 4, high, true);
};

export class Bw64Writer {
    // config:
    //   writable       - FileSystemWritableFileStream (opened for writing)
    //   sampleRate     - Hz (nominal, from the FLAC header)
    //   channels       - channel count
    //   bitsPerSample  - 8 / 16 / 24 / 32
    //   float          - true => samples are IEEE 32-bit float (not integer PCM)
    //   bext           - { description, originator, originatorReference,
    //                      originationDate ("YYYY-MM-DD"), originationTime
    //                      ("HH:MM:SS"), timeReferenceSamples (Number) }
    //   ixml           - optional string embedded as an <iXML> chunk (carries the
    //                    full chronos sidecar, incl. the measured/drift rate that
    //                    bext cannot express)
    constructor(config) {
        this.writable = config.writable;
        this.sampleRate = config.sampleRate;
        this.channels = config.channels;
        this.bitsPerSample = config.bitsPerSample;
        this.float = !!config.float;
        this.bext = config.bext || {};
        this.ixml = config.ixml || null;

        this.bytesPerSample = this.bitsPerSample >> 3;
        this.blockAlign = this.channels * this.bytesPerSample;

        this.dataBytes = 0;   // PCM bytes streamed so far
        this.headerBytes = 0; // total header size (offset of first PCM byte)

        // Byte offsets of the ds64 size fields, filled in during writeHeader().
        this._ds64RiffSizeOffset = 0;
        this._ds64DataSizeOffset = 0;
        this._ds64SampleCountOffset = 0;
    }

    // Assembles and writes the complete header up to (and including) the `data`
    // chunk size field, then leaves the stream positioned for PCM.
    async writeHeader() {
        const enc = new TextEncoder();

        // --- iXML chunk payload (optional) ---
        let ixmlBytes = null;
        if (this.ixml) {
            ixmlBytes = enc.encode(this.ixml);
        }
        const ixmlChunkLen = ixmlBytes
            ? 8 + ixmlBytes.length + (ixmlBytes.length & 1) // +pad to even
            : 0;

        // --- fixed chunk sizes ---
        const ds64BodyLen = 28;   // riffSize + dataSize + sampleCount + tableLen(0)
        const fmtBodyLen = 40;    // WAVE_FORMAT_EXTENSIBLE
        const bextBodyLen = 602;  // bext v1, empty CodingHistory

        const headerLen =
            12 +                    // 'RF64' + size + 'WAVE'
            (8 + ds64BodyLen) +
            (8 + fmtBodyLen) +
            (8 + bextBodyLen) +
            ixmlChunkLen +
            8;                      // 'data' + size (PCM follows)

        const buffer = new ArrayBuffer(headerLen);
        const view = new DataView(buffer);
        const bytes = new Uint8Array(buffer);
        let o = 0;

        const putTag = (tag) => {
            for (let i = 0; i < 4; i++) view.setUint8(o + i, tag.charCodeAt(i));
            o += 4;
        };

        // ---- RF64 / WAVE ----
        putTag('RF64');
        view.setUint32(o, RF64_SENTINEL, true); o += 4; // real size -> ds64.riffSize
        putTag('WAVE');

        // ---- ds64 ----
        putTag('ds64');
        view.setUint32(o, ds64BodyLen, true); o += 4;
        this._ds64RiffSizeOffset = o;      writeUint64LE(view, o, 0); o += 8;
        this._ds64DataSizeOffset = o;      writeUint64LE(view, o, 0); o += 8;
        this._ds64SampleCountOffset = o;   writeUint64LE(view, o, 0); o += 8;
        view.setUint32(o, 0, true); o += 4; // table length

        // ---- fmt  (WAVE_FORMAT_EXTENSIBLE) ----
        putTag('fmt ');
        view.setUint32(o, fmtBodyLen, true); o += 4;
        view.setUint16(o, 0xfffe, true); o += 2;                       // wFormatTag = EXTENSIBLE
        view.setUint16(o, this.channels, true); o += 2;
        view.setUint32(o, this.sampleRate, true); o += 4;
        view.setUint32(o, this.sampleRate * this.blockAlign, true); o += 4; // byte rate
        view.setUint16(o, this.blockAlign, true); o += 2;
        view.setUint16(o, this.bitsPerSample, true); o += 2;           // container bits
        view.setUint16(o, 22, true); o += 2;                           // cbSize
        view.setUint16(o, this.bitsPerSample, true); o += 2;           // valid bits
        view.setUint32(o, 0, true); o += 4;                            // channel mask
        // SubFormat GUID: KSDATAFORMAT_SUBTYPE_PCM   (00000001-0000-0010-8000-00aa00389b71)
        //             or  KSDATAFORMAT_SUBTYPE_IEEE_FLOAT (00000003-...) when float.
        view.setUint32(o, this.float ? 0x00000003 : 0x00000001, true); o += 4;
        view.setUint16(o, 0x0000, true); o += 2;
        view.setUint16(o, 0x0010, true); o += 2;
        const pcmGuidTail = [0x80, 0x00, 0x00, 0xaa, 0x00, 0x38, 0x9b, 0x71];
        for (let i = 0; i < 8; i++) { view.setUint8(o + i, pcmGuidTail[i]); }
        o += 8;

        // ---- bext (v1) ----
        putTag('bext');
        view.setUint32(o, bextBodyLen, true); o += 4;
        const bextStart = o;
        writeFixedAscii(view, bextStart + 0, this.bext.description, 256);
        writeFixedAscii(view, bextStart + 256, this.bext.originator, 32);
        writeFixedAscii(view, bextStart + 288, this.bext.originatorReference, 32);
        writeFixedAscii(view, bextStart + 320, this.bext.originationDate, 10);
        writeFixedAscii(view, bextStart + 330, this.bext.originationTime, 8);
        const timeRef = Math.max(0, Math.round(this.bext.timeReferenceSamples || 0));
        writeUint64LE(view, bextStart + 338, timeRef); // TimeReferenceLow/High
        view.setUint16(bextStart + 346, 1, true);       // Version = 1 (UMID present)
        // UMID[64] + loudness fields + reserved[180] stay zero.
        o = bextStart + bextBodyLen;

        // ---- iXML (optional) ----
        if (ixmlBytes) {
            putTag('iXML');
            view.setUint32(o, ixmlBytes.length, true); o += 4;
            bytes.set(ixmlBytes, o); o += ixmlBytes.length;
            if (ixmlBytes.length & 1) { view.setUint8(o, 0); o += 1; } // pad
        }

        // ---- data (size is RF64 sentinel; PCM streamed after) ----
        putTag('data');
        view.setUint32(o, RF64_SENTINEL, true); o += 4;

        this.headerBytes = headerLen;
        await this.writable.write(bytes);
    }

    // Appends a PCM payload (already interleaved, little-endian) to the data chunk.
    async writePcm(uint8) {
        if (uint8 && uint8.length) {
            await this.writable.write(uint8);
            this.dataBytes += uint8.length;
        }
    }

    // Pads the data chunk to even length if needed, then patches the ds64 size
    // fields with the now-known totals by seeking back to their offsets.
    async finalize() {
        if (this.dataBytes & 1) {
            await this.writable.write(new Uint8Array([0]));
            // The RIFF pad byte is not counted in the data size, but it is part of
            // the file, so include it in the RIFF size below.
        }
        const padBytes = this.dataBytes & 1 ? 1 : 0;
        const riffSize = this.headerBytes + this.dataBytes + padBytes - 8; // exclude 'RF64'+size
        const sampleCount = this.blockAlign > 0 ? Math.floor(this.dataBytes / this.blockAlign) : 0;

        const patch = new ArrayBuffer(8);
        const pv = new DataView(patch);

        writeUint64LE(pv, 0, riffSize);
        await this.writable.write({ type: 'write', position: this._ds64RiffSizeOffset, data: patch.slice(0) });

        writeUint64LE(pv, 0, this.dataBytes);
        await this.writable.write({ type: 'write', position: this._ds64DataSizeOffset, data: patch.slice(0) });

        writeUint64LE(pv, 0, sampleCount);
        await this.writable.write({ type: 'write', position: this._ds64SampleCountOffset, data: patch.slice(0) });
    }
}

// Derives the bext OriginationDate / OriginationTime / TimeReference (samples
// since midnight UTC) from an ISO-8601 start timestamp. The sub-second part is
// dropped from the HH:MM:SS text but preserved in the sample-accurate
// TimeReference. Returns zeros/empties for a missing/unparseable input.
export const bextTimingFromUtc = (startUtcIso, sampleRate) => {
    if (!startUtcIso) return { originationDate: '', originationTime: '', timeReferenceSamples: 0 };
    const d = new Date(startUtcIso);
    if (isNaN(d.getTime())) return { originationDate: '', originationTime: '', timeReferenceSamples: 0 };

    const pad = (n, w = 2) => String(n).padStart(w, '0');
    const originationDate = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
    const originationTime = `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;

    const midnight = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    const deltaSeconds = (d.getTime() - midnight) / 1000;
    const timeReferenceSamples = Math.round(deltaSeconds * (sampleRate || 0));

    return { originationDate, originationTime, timeReferenceSamples };
};
